import { ApiService } from '../api/api.service';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { AxiosInstance, AxiosResponse } from 'axios';
import { Logger } from '../logger';
import { MUTATIONS as CUSTOMER_MUTATIONS } from '@/store/customer';
import { SustainabilityMetrics, OrderAndHistoryMetricsRequest, OrderAndHistorySustainabilityMetrics } from '@/models/sustainability';
import { getStartOfYear } from '@/helpers';
import { AnalyticsService } from '../analytics/analytics.service';

const BASE_ROUTE = '/sustainability/v1';

export interface SustainabilityService {
  getHistorySustainabilityMetrics(): Promise<void>;
  postCurrentOrderAndHistorySustainabilityMetrics(request: OrderAndHistoryMetricsRequest): Promise<OrderAndHistorySustainabilityMetrics>;
  setMobileSustainabilityTooltipAppeared(): void;
  getHistorySustainabilityOrder(orderId: string): Promise<SustainabilityMetrics>;
  setSustainabilityOrderMetrics(metrics: SustainabilityMetrics | null): void
}

export class AppSustainabilityService extends ApiService implements SustainabilityService {
  constructor(protected store: Store<AppState>, protected httpClient: AxiosInstance, protected logger: Logger, analytics: AnalyticsService) {
    super(store, httpClient, logger, analytics);
  }

  async getHistorySustainabilityMetrics(): Promise<void> {
    try {
      const currentYear = new Date().getFullYear();
      const response: SustainabilityMetrics = ((await this.httpClient.get(`${BASE_ROUTE}/sustainability/summary/${currentYear}`)) as AxiosResponse<SustainabilityMetrics>).data;

      this.setSustainabilityHistoryMetrics(response);
    } catch (e: any) {
      this.logAndThrowApiError('getSustainabilityHistoryMetrics', e); 
    }
  }

  async getHistorySustainabilityOrder(orderId: string): Promise<SustainabilityMetrics> {
    try{
      const response = ((await this.httpClient.get(`${BASE_ROUTE}/sustainability/${orderId}`)))
      return response.data
    } catch (e: any) {
      this.logAndThrowApiError('getHistorySustainabilityOrder', e)
    }
  }

  async postCurrentOrderAndHistorySustainabilityMetrics(request: OrderAndHistoryMetricsRequest): Promise<OrderAndHistorySustainabilityMetrics> {
    let response: OrderAndHistorySustainabilityMetrics;
   
    try {
      response = ((await this.httpClient.post(`${BASE_ROUTE}/sustainability`, request)) as AxiosResponse<OrderAndHistorySustainabilityMetrics>).data;
    } catch (e: any) {
      return this.logAndThrowApiError('getCurrentOrderAndHistoryMetrics', e);
    }

    await this.setOrderAndHistorySustainabilityMetrics(response);

    return response;
  }

  setSustainabilityOrderMetrics(metrics: SustainabilityMetrics | null): void {
    this.store.commit(CUSTOMER_MUTATIONS.SET_SUSTAINABILITY_ORDER_METRICS, metrics);
  }

  setSustainabilityHistoryMetrics(metrics: SustainabilityMetrics | null): void {
    this.store.commit(CUSTOMER_MUTATIONS.SET_SUSTAINABILITY_HISTORY_METRICS, metrics);
  }

  async setOrderAndHistorySustainabilityMetrics(metrics: OrderAndHistorySustainabilityMetrics | null): Promise<void> {
    this.setSustainabilityOrderMetrics(metrics ? metrics.order : null);
    
    if (metrics?.history) {
      this.setSustainabilityHistoryMetrics(metrics.history);
    } else if (!this.store.state.customer.sustainabilityMetrics.history) {
      await this.getHistorySustainabilityMetrics();
    }
  }

  // On mobile, tooltip only appears one time per session.
  setMobileSustainabilityTooltipAppeared(): void {
    this.store.commit(CUSTOMER_MUTATIONS.SET_SUSTAINABILITY_TOOLTIP_APPEARED, true);
  }
}
